/**
 * Email Compose — page-specific client bootstrap (EE6).
 *
 * Owns the wizard's "Review & Send" preview pane. On the compose page the
 * `[data-email-preview]` container declares `data-email-preview-managed='true'`,
 * which makes the GENERIC preview binder in dashboard/client.js skip it — this
 * module is the sole owner, so there is never a double-bind.
 *
 * What it adds over the generic (iframe-only) binder:
 *   - Desktop / Mobile width toggles for the rendered email.
 *   - A Plain-text pane (the text/plain alternative the sender emits), from the
 *     preview action's `text`.
 *   - A "Refresh preview" control.
 *   - A "Send Test" button.
 *
 * SAFETY: it calls ONLY the page's own `action` handler with `preview` (read-only
 * render) and `send-test`. The server's send-test resolves the CURRENT USER's own
 * address and never reads a segment or dispatches the blast — this client cannot
 * choose a recipient. The rendered email HTML is injected ONLY into a sandboxed
 * <iframe srcdoc> (no allow-scripts), never into the dashboard DOM.
 */

// Rendered email markup is document-level HTML with its own inline CSS — it goes
// into a sandboxed iframe. allow-same-origin (WITHOUT allow-scripts) lets the frame
// be measured for auto-height while keeping the markup unable to execute.
var IFRAME_SANDBOX = "allow-same-origin";

function esc(value) {
  return (value == null ? "" : value.toString())
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

// DashboardAPI.action() sometimes returns the handler result at the top level and
// sometimes nested under `.data` (mirrors the generic binder's own html lookup).
function pick(resp, key) {
  if (resp && resp[key] !== undefined) return resp[key];
  if (resp && resp.data && resp.data[key] !== undefined) return resp.data[key];
  return undefined;
}

function firstError(resp) {
  var errs = pick(resp, "errors");
  if (errs instanceof Array && errs.length && errs[0]) return errs[0];
  return null;
}

function setStatus(node, kind, message) {
  if (!node) return;
  node.textContent = (message || "").toString();
  node.className = "egt-email-compose__preview-status" +
    (kind ? (" is-" + kind) : "");
  node.style.display = message ? "block" : "none";
}

function makeButton(label, cls) {
  var b = document.createElement("button");
  b.type = "button";
  b.className = cls;
  b.textContent = label;
  return b;
}

module.exports = async function(context) {
  var ctx = context || {};

  // GUARD: only act on a page that renders the managed compose preview container.
  var host = document.querySelector("[data-email-preview][data-email-preview-managed='true']");
  if (!host) return;
  if (host.getAttribute("data-email-compose-preview-bound") === "true") return;
  host.setAttribute("data-email-compose-preview-bound", "true");

  var dashboardApi = ctx.dashboardApi ||
    (window.DashboardAPI || ((((window.UATools || {}).dashboard || {}).api) || null));
  var dashCtx = ctx.dashboard || ctx.context || {};
  var currentPage = dashCtx.currentPage || {};
  var pageNamespace = (currentPage.namespace || "").toString().trim();
  var apiUrl = ((currentPage.routes || {}).api || "").toString().trim() || null;

  var recordId = (host.getAttribute("data-email-preview") || "").toString().trim();
  if (!recordId || recordId === "new") {
    recordId = ((dashCtx.currentRecordId) || "").toString().trim();
  }

  var userEmail = (((dashCtx.user || {}).email) || "").toString().trim();

  // Nothing saved yet → no compiled body to preview or test. Show a note only.
  if (!dashboardApi || !pageNamespace || !recordId || recordId === "new") {
    host.innerHTML = "";
    var note = document.createElement("p");
    note.className = "small text-muted mb-0";
    note.textContent = "Save this draft to see a live preview and send yourself a test.";
    host.appendChild(note);
    return;
  }

  // ── Build the toolbar + stage once ─────────────────────────────────────────
  host.innerHTML = "";
  host.setAttribute("data-preview-mode", "desktop");

  var toolbar = document.createElement("div");
  toolbar.className = "egt-email-compose__preview-toolbar";

  var modeGroup = document.createElement("div");
  modeGroup.className = "egt-email-compose__preview-modes";
  modeGroup.setAttribute("role", "group");
  modeGroup.setAttribute("aria-label", "Preview width");
  var btnDesktop = makeButton("Desktop", "btn btn-sm egt-email-compose__preview-mode is-active");
  var btnMobile = makeButton("Mobile", "btn btn-sm egt-email-compose__preview-mode");
  var btnPlain = makeButton("Plain text", "btn btn-sm egt-email-compose__preview-mode");
  modeGroup.appendChild(btnDesktop);
  modeGroup.appendChild(btnMobile);
  modeGroup.appendChild(btnPlain);

  var spacer = document.createElement("div");
  spacer.className = "egt-email-compose__preview-spacer";

  var btnRefresh = makeButton("Refresh preview", "btn btn-sm egt-email-compose__preview-refresh");
  var btnTest = makeButton(
    userEmail ? ("Send test to " + userEmail) : "Send test to yourself",
    "btn btn-sm egt-email-compose__preview-test"
  );
  btnTest.title = "Sends this email only to your own signed-in address" +
    (userEmail ? (" (" + userEmail + ")") : "") + ". No subscriber or segment is ever contacted.";

  toolbar.appendChild(modeGroup);
  toolbar.appendChild(spacer);
  toolbar.appendChild(btnRefresh);
  toolbar.appendChild(btnTest);

  var status = document.createElement("div");
  status.className = "egt-email-compose__preview-status";
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  status.style.display = "none";

  var stage = document.createElement("div");
  stage.className = "egt-email-compose__preview-stage";

  var frame = document.createElement("iframe");
  frame.setAttribute("sandbox", IFRAME_SANDBOX);
  frame.setAttribute("referrerpolicy", "no-referrer");
  frame.setAttribute("title", "Email preview");
  frame.setAttribute("loading", "lazy");
  frame.className = "egt-email-compose__preview-frame";
  frame.addEventListener("load", function() {
    try {
      var d = frame.contentDocument;
      if (!d || !d.body) return;
      var height = Math.max(
        d.body.scrollHeight || 0,
        d.documentElement ? (d.documentElement.scrollHeight || 0) : 0
      );
      if (height > 0) frame.style.height = (height / 16) + "rem";
    } catch (measureError) { /* opaque doc — keep the CSS min-height fallback */ }
  });

  var plain = document.createElement("pre");
  plain.className = "egt-email-compose__preview-plain";
  plain.style.display = "none";

  stage.appendChild(frame);
  stage.appendChild(plain);

  host.appendChild(toolbar);
  host.appendChild(status);
  host.appendChild(stage);

  // ── State ──────────────────────────────────────────────────────────────────
  var lastHtml = null;
  var lastText = "";
  var currentMode = "desktop";

  function applyMode(mode) {
    currentMode = mode;
    host.setAttribute("data-preview-mode", mode);
    btnDesktop.classList.toggle("is-active", mode === "desktop");
    btnMobile.classList.toggle("is-active", mode === "mobile");
    btnPlain.classList.toggle("is-active", mode === "plain");
    if (mode === "plain") {
      frame.style.display = "none";
      plain.style.display = "block";
    } else {
      plain.style.display = "none";
      frame.style.display = "block";
    }
  }

  function renderInto() {
    if (typeof lastHtml === "string") {
      // Re-assigning srcdoc reloads the frame and re-fires the height measurement.
      frame.setAttribute("srcdoc", lastHtml);
    }
    plain.textContent = lastText || "(No plain-text alternative was produced.)";
  }

  async function loadPreview() {
    setStatus(status, "loading", "Loading preview…");
    btnTest.disabled = true;
    var payload = { action: "preview", id: recordId, docId: recordId };
    var resp = null;
    try {
      resp = apiUrl
        ? await dashboardApi.action(pageNamespace, "action", payload, apiUrl)
        : await dashboardApi.action(pageNamespace, "action", payload);
    } catch (previewError) {
      var e = (previewError && (previewError.errors || [])[0]) || previewError || {};
      setStatus(status, "error",
        (e.title ? (e.title + " — ") : "") +
        (e.description || e.message || "Preview is unavailable right now."));
      return;
    }

    var errObj = firstError(resp);
    var html = pick(resp, "html");
    if (pick(resp, "success") === false || typeof html !== "string" || !html) {
      setStatus(status, "error",
        errObj
          ? ((errObj.title ? (errObj.title + " — ") : "") + (errObj.description || ""))
          : "No preview available for this draft yet.");
      return;
    }

    lastHtml = html;
    var text = pick(resp, "text");
    lastText = (typeof text === "string") ? text : "";
    renderInto();
    applyMode(currentMode);
    btnTest.disabled = false;
    setStatus(status, "", "");
  }

  async function sendTest() {
    btnTest.disabled = true;
    setStatus(status, "loading", "Sending a test to your own inbox…");
    var payload = { action: "send-test", id: recordId, docId: recordId };
    var resp = null;
    try {
      resp = apiUrl
        ? await dashboardApi.action(pageNamespace, "action", payload, apiUrl)
        : await dashboardApi.action(pageNamespace, "action", payload);
    } catch (sendError) {
      var e = (sendError && (sendError.errors || [])[0]) || sendError || {};
      setStatus(status, "error",
        (e.title ? (e.title + " — ") : "") +
        (e.description || e.message || "The test could not be sent."));
      btnTest.disabled = false;
      return;
    }

    var errObj = firstError(resp);
    if (pick(resp, "success") === false || errObj) {
      setStatus(status, "error",
        errObj
          ? ((errObj.title ? (errObj.title + " — ") : "") + (errObj.description || ""))
          : "The test could not be sent.");
      btnTest.disabled = false;
      return;
    }

    var sentTo = pick(resp, "sentTo") || userEmail;
    setStatus(status, "success",
      sentTo ? ("Test sent to your inbox: " + sentTo + ".") : "Test sent to your inbox.");
    btnTest.disabled = false;
  }

  btnDesktop.addEventListener("click", function() { applyMode("desktop"); });
  btnMobile.addEventListener("click", function() { applyMode("mobile"); });
  btnPlain.addEventListener("click", function() { applyMode("plain"); });
  btnRefresh.addEventListener("click", function() { loadPreview(); });
  btnTest.addEventListener("click", function() { sendTest(); });

  applyMode("desktop");
  await loadPreview();
};
